import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CartItem } from "@hatch-digital-nl/bravilor-storybook";
import {
    useGetPendingOrderQuery,
    useGetRefurbishedProductsQuery,
    useRemoveReservedRefurbishedProductMutation,
    useReserveRefurbishedProductMutation,
} from "../../../common/api/refurbished";
import { useStringTranslation } from "../../../common/hooks/useStringTranslation";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { RouteEnum } from "../../../common/enums/RouteEnum";
import { setCartOpen, toggleCartOpen } from "../CartSlice";
import { RefurbishedPendingOrderInterface } from "../../../common/interfaces/apiV2/refurbished/RefurbishedPendingOrderInterface";
import { CartTimerStatus } from "../utils/cartTimer";
import { CartLabels, getCartLabels } from "../utils/cartLabels";
import { getCartItemsFromPendingOrder } from "../utils/getCartItemsFromPendingOrder";
import { useCartTimer } from "./useCartTimer";
import { useExpiredCartCleanup } from "./useExpiredCartCleanup";
import { useCartProductActions } from "./useCartProductActions";

export type { CartLabels };

export interface UseCartReturn {
    pendingOrder: RefurbishedPendingOrderInterface | null;
    cartItems: CartItem[];
    count: number;
    timerDisplay: string;
    timerStatus: CartTimerStatus;
    reservationTime: string;
    isCartOpen: boolean;
    isPendingOrderLoading: boolean;
    isReserving: boolean;
    isRemoving: boolean;
    isReservingProduct: (productId: number) => boolean;
    isRemovingProduct: (productId: number) => boolean;
    removingItemId: string | null;
    cartLabels: CartLabels;
    toggleCartOpen: () => void;
    setCartOpen: (open: boolean) => void;
    reserveProductById: (productId: number) => Promise<void>;
    removeProductById: (productId: number) => Promise<void>;
    handleCheckoutFromModal: () => void;
    handleCheckoutFromPage: () => void;
}

export const useCart = (): UseCartReturn => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { isCartOpen } = useAppSelector((state) => state.cart);
    const { t } = useStringTranslation();

    const {
        data: pendingOrderResponse,
        refetch: refetchPendingOrder,
        isLoading: isPendingOrderLoading,
    } = useGetPendingOrderQuery();

    const { refetch: refetchProducts } = useGetRefurbishedProductsQuery({
        filter: {},
    });

    const [reserveProduct, { isLoading: isReserving }] =
        useReserveRefurbishedProductMutation();
    const [removeReservedProduct, { isLoading: isRemoving }] =
        useRemoveReservedRefurbishedProductMutation();

    const pendingOrder = pendingOrderResponse?.data ?? null;
    const timer = useCartTimer(pendingOrder?.expires_at);

    useExpiredCartCleanup({
        pendingOrder,
        timer,
        removeReservedProduct,
        refetchPendingOrder,
        refetchProducts,
    });

    const cartItems = useMemo(
        () => getCartItemsFromPendingOrder(pendingOrder),
        [pendingOrder],
    );

    const cartLabels = useMemo(
        () => getCartLabels(t, pendingOrder?.order_lines.length ?? 0),
        [t, pendingOrder?.order_lines.length],
    );

    const {
        isReservingProduct,
        isRemovingProduct,
        removingItemId,
        reserveProductById,
        removeProductById,
    } = useCartProductActions({
        cartLabels,
        reserveProduct,
        removeReservedProduct,
        isReserving,
        isRemoving,
    });

    const handleCheckoutFromModal = useCallback(() => {
        dispatch(setCartOpen(false));
        navigate(RouteEnum.REFURBISHED_CART);
    }, [dispatch, navigate]);

    const handleCheckoutFromPage = useCallback(() => {
        navigate(RouteEnum.REFURBISHED_CHECKOUT);
    }, [navigate]);

    return {
        pendingOrder,
        cartItems,
        count: cartItems.length,
        timerDisplay: timer.display,
        timerStatus: timer.status,
        reservationTime: timer.display,
        isCartOpen,
        isPendingOrderLoading,
        isReserving,
        isRemoving,
        isReservingProduct,
        isRemovingProduct,
        removingItemId,
        cartLabels,
        toggleCartOpen: () => dispatch(toggleCartOpen()),
        setCartOpen: (open: boolean) => dispatch(setCartOpen(open)),
        reserveProductById,
        removeProductById,
        handleCheckoutFromModal,
        handleCheckoutFromPage,
    };
};
